import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KnowledgeSource, KnowledgeSourceConfigType } from '@vendasta/embeddings';
import { AiKnowledgeService } from '../ai-knowledge.service';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { KnowledgeEditorComponent } from '../knowledge-editor/knowledge-editor.component';
import { MatDialog, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { firstValueFrom, map } from 'rxjs';
import { DeleteCustomKnowledgeComponent } from '../delete-custom-knowledge/delete-custom-knowledge.component';
import { LearnMoreBusinessProfileComponent } from '../learn-more-business-profile/learn-more-business-profile.component';
import { MatCardModule } from '@angular/material/card';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { KnowledgeListComponent } from '../knowledge-list/knowledge-list.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SHOW_BUSINESS_PROFILE_SOURCE_TOKEN } from '../tokens';
import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { STEP_OPTION } from '../knowledge-editor/website-scrape/scraped-pages/scraped-pages.component';

@Component({
  selector: 'ai-knowledge-knowledge-source-management',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    GalaxyPageModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    MatListModule,
    MatCardModule,
    KnowledgeEditorComponent,
    DeleteCustomKnowledgeComponent,
    LearnMoreBusinessProfileComponent,
    MatDialogModule,
    KnowledgeListComponent,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './knowledge-source-management.component.html',
  styleUrls: ['./knowledge-source-management.component.scss'],
})
export class KnowledgeSourceManagementComponent implements OnInit {
  @Input() previousPageTitle?: string;
  @Input() previousPageUrl?: string;
  DIALOG_WIDTH = '660px';
  SCRAPED_PAGES_STEP_OPTION = STEP_OPTION;
  private readonly dialog = inject(MatDialog);
  private readonly bpObserver = inject(BreakpointObserver);
  private readonly activeRoute = inject(ActivatedRoute);

  readonly knowledgeSources = signal<KnowledgeSource[]>([]);
  private readonly showBusinessProfileSource = toSignal(inject(SHOW_BUSINESS_PROFILE_SOURCE_TOKEN));
  private knowledgeService = inject(AiKnowledgeService);
  confirmationModal = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);
  router = inject(Router);

  readonly isLoading = signal(true);

  async ngOnInit(): Promise<void> {
    await this.refreshSources();
    const editSourceParam = this.activeRoute.snapshot.queryParamMap.get('editSource');
    if (editSourceParam) {
      const [sourceId, option] = this.sourceIdAndOptionFromEditSourceParam(editSourceParam);
      const source = this.knowledgeSources().find((s) => s.id == sourceId);
      if (source) {
        await this.editKnowledgeSource(source, option);
      }
    }
    if (this.activeRoute.snapshot.queryParamMap.get('addSource')) {
      await this.addNewKnowledgeSource();
    }
  }

  sourceIdAndOptionFromEditSourceParam(editSourceParam: string): [string, string] {
    const colonIndex = editSourceParam.indexOf(':');
    if (colonIndex === -1) {
      return [editSourceParam, ''];
    } else {
      return [editSourceParam.substring(0, colonIndex), editSourceParam.substring(colonIndex + 1)];
    }
  }

  async refreshSources(): Promise<void> {
    this.isLoading.set(true);
    const result = await this.knowledgeService.listKnowledgeSources([
      KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_CUSTOM_DATA,
      KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_WEBSITE_SCRAPE,
    ]);
    const knowledgeSources = result.knowledgeSources || [];
    if (this.showBusinessProfileSource()) {
      // manually insert business profile source because it may not exist yet
      const businessProfileSource = this.knowledgeService.buildBusinessProfileKnowledgeSource();
      if (businessProfileSource) {
        knowledgeSources.unshift(businessProfileSource);
      }
    }
    this.knowledgeSources.set(knowledgeSources);
    this.isLoading.set(false);
  }

  async addNewKnowledgeSource(): Promise<void> {
    const conf: MatDialogConfig = {
      width: this.DIALOG_WIDTH,
    };

    const isSmallScreen = this.bpObserver.isMatched(Breakpoints.XSmall);
    if (isSmallScreen) {
      conf.maxWidth = '100vw';
      conf.maxHeight = '100vh';
    }
    const result = await firstValueFrom(this.dialog.open(KnowledgeEditorComponent, conf).afterClosed());
    if (result) {
      this.openSuggestInboxSettingsModal();
    }
    await this.refreshSources();
  }

  openSuggestInboxSettingsModal() {
    this.confirmationModal
      .openModal({
        title: this.translateService.instant('INBOX_SETTINGS.PROMPT'),
        message: this.translateService.instant('INBOX_SETTINGS.DESCRIPTION'),
        confirmButtonText: this.translateService.instant('INBOX_SETTINGS.GO_TO'),
        cancelButtonText: this.translateService.instant('COMMON.CLOSE'),
      })
      .subscribe((userDidAction) => {
        if (userDidAction) {
          this.router.navigate(this.knowledgeService.buildInboxSettingsRoute());
        }
      });
  }

  async editKnowledgeSource(source: KnowledgeSource, option?: string): Promise<void> {
    if (source?.config?.businessProfileConfig?.accountGroupId) {
      this.dialog.open(LearnMoreBusinessProfileComponent, {
        width: this.DIALOG_WIDTH,
      });
      return;
    }
    const conf: MatDialogConfig = {
      width: this.DIALOG_WIDTH,
      data: { knowledgeSourceKey: { knowledgeSourceId: source?.id, namespace: source?.namespace }, stepOption: option },
    };

    const isSmallScreen = this.bpObserver.isMatched(Breakpoints.XSmall);
    if (isSmallScreen) {
      conf.maxWidth = '100vw';
      conf.maxHeight = '100vh';
    }

    const editor = this.dialog.open(KnowledgeEditorComponent, conf);
    editor.componentInstance.stepChange
      .pipe(
        takeUntilDestroyed(editor.componentInstance.destroyRef),
        map((stepOption) => this.setEditSourceQueryParam(source?.id, stepOption)),
      )
      .subscribe();

    const result = await firstValueFrom(editor.afterClosed());
    this.setEditSourceQueryParam(null);
    if (result) {
      source = await this.knowledgeService.getKnowledgeSource(source?.id, source?.namespace, true);
      if (!source?.knowledgeBaseKeys || source?.knowledgeBaseKeys?.length === 0) {
        this.openSuggestInboxSettingsModal();
      }
    }
    await this.refreshSources();
  }

  async deleteKnowledgeSource(source: KnowledgeSource): Promise<void> {
    const result = await firstValueFrom(
      this.dialog
        .open(DeleteCustomKnowledgeComponent, {
          width: this.DIALOG_WIDTH,
          data: { knowledgeSourceId: source.id },
        })
        .afterClosed(),
    );
    if (result) {
      await this.refreshSources();
    }
  }

  setEditSourceQueryParam(editSource: string | null, option?: string): void {
    if (option) {
      editSource = editSource + ':' + option;
    }
    const queryParams = { editSource: editSource };
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
